import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Lock, Sparkles, PenLine, Image as ImageIcon } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import WriterKnowledge from "@/components/brand/WriterKnowledge";

export default function AIInstructionsPanel({ brand, role, onUpdated }) {
  const { user } = useAuth();
  const { t } = useI18n();

  const TOOLS = useMemo(() => ([
    { key: "assistant", title: t("settings.ai_assistant_title"), Icon: Sparkles, sub: t("settings.ai_assistant_sub"), placeholder: t("settings.ai_assistant_placeholder") },
    { key: "writer",    title: t("settings.ai_writer_title"),    Icon: PenLine,  sub: t("settings.ai_writer_sub"),    placeholder: t("settings.ai_writer_placeholder") },
    { key: "creator",   title: t("settings.ai_creator_title"),   Icon: ImageIcon,sub: t("settings.ai_creator_sub"),   placeholder: t("settings.ai_creator_placeholder") },
  ]), [t]);

  const isPrivileged = useMemo(() => {
    if (!user) return false;
    if (brand.owner_user_id === user.user_id) return true;
    const r = role || brand.role;
    return r === "owner" || r === "manager";
  }, [user, brand, role]);

  const initial = useMemo(
    () => ({
      assistant: brand.ai_instructions?.assistant || "",
      writer: brand.ai_instructions?.writer || "",
      creator: brand.ai_instructions?.creator || "",
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [brand.id, brand.ai_instructions?.assistant, brand.ai_instructions?.writer, brand.ai_instructions?.creator],
  );
  const [values, setValues] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [members, setMembers] = useState([]);
  const [targetManager, setTargetManager] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => { setValues(initial); }, [initial]);

  useEffect(() => {
    (async () => {
      const url = isPrivileged ? `/brands/${brand.id}/members` : `/brands/${brand.id}/managers`;
      try {
        const { data } = await api.get(url);
        setMembers(data);
        const first = data.find((m) => m.role === "manager" || m.role === "owner");
        if (first) setTargetManager(first.user_id);
      } catch { /* ignore */ }
    })();
  }, [brand.id, isPrivileged]);

  const dirty =
    values.assistant !== initial.assistant ||
    values.writer !== initial.writer ||
    values.creator !== initial.creator;

  const save = async () => {
    if (!dirty) return;
    setBusy(true);
    try {
      await api.put(`/brands/${brand.id}`, { ai_instructions: values });
      toast.success(t("settings.ai_saved"));
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("settings.ai_save_failed"));
    } finally {
      setBusy(false);
    }
  };

  const sendRequest = async () => {
    if (!targetManager) {
      toast.error(t("settings.ai_pick_manager_first"));
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/brands/${brand.id}/request-ai-access`, { manager_user_id: targetManager });
      const name = data?.manager_name || "the manager";
      toast.success(t("settings.ai_request_sent").replace("{name}", name));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("settings.ai_request_failed"));
    } finally {
      setSending(false);
    }
  };

  const managers = members.filter((m) => m.role === "manager" || m.role === "owner");

  const cards = (
    <div className="space-y-5">
      {TOOLS.map(({ key, title, sub, Icon, placeholder }) => (
        <div
          key={key}
          className="rounded-xl border border-slate-100 bg-[#fcfcfc] p-5"
          data-testid={`ai-instructions-card-${key}`}
        >
          <div className="flex items-center gap-2.5 mb-1">
            <div className="w-8 h-8 rounded-md bg-slate-900 text-white flex items-center justify-center">
              <Icon className="w-4 h-4" />
            </div>
            <div>
              <div className="text-sm font-medium text-slate-900">{title}</div>
              <div className="text-xs text-slate-500">{sub}</div>
            </div>
          </div>
          <Label className="sr-only">{title}</Label>
          <textarea
            data-testid={`ai-instructions-${key}-textarea`}
            value={values[key]}
            onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
            placeholder={placeholder}
            maxLength={4000}
            rows={6}
            disabled={!isPrivileged}
            className="mt-3 w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[var(--bh-accent,#fe7f57)] resize-y min-h-[120px] transition-colors"
          />
          <div className="text-[11px] text-slate-400 mt-1 text-right tabular-nums">
            {values[key].length} / 4000
          </div>
          {key === "writer" && (
            <WriterKnowledge brandId={brand.id} disabled={!isPrivileged} />
          )}
        </div>
      ))}
    </div>
  );

  const inactiveSave = !dirty || busy;

  return (
    <section
      data-testid="ai-instructions-panel"
      className="rounded-xl border border-gray-200 bg-white p-6 md:p-8"
    >
      <header className="flex items-start justify-between gap-4 mb-6">
        <div className="min-w-0">
          <h2 className="font-display text-2xl font-medium text-[#1a1a1a]">{t("settings.ai_title")}</h2>
          <p className="text-sm text-gray-500 mt-0.5">{t("settings.ai_sub")}</p>
        </div>
        {isPrivileged && (
          <Button
            onClick={save}
            disabled={inactiveSave}
            data-testid="ai-instructions-save"
            className={`h-10 rounded-lg shrink-0 px-4 transition-colors ${
              inactiveSave
                ? "bg-gray-100 text-gray-400 hover:bg-gray-100 cursor-not-allowed"
                : "bg-slate-900 text-white hover:bg-slate-800"
            }`}
          >
            {busy ? t("settings.ai_saving") : t("settings.ai_save")}
          </Button>
        )}
      </header>

      {isPrivileged ? (
        cards
      ) : (
        <div className="relative">
          <div className="pointer-events-none select-none blur-md opacity-60" aria-hidden="true">
            {cards}
          </div>
          <div className="absolute inset-0 flex items-start justify-center pt-16">
            <div
              data-testid="ai-instructions-access-card"
              className="w-full max-w-md bg-white rounded-xl shadow-xl border border-slate-100 p-6"
            >
              <div className="w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center mb-4">
                <Lock className="w-5 h-5 text-slate-700" strokeWidth={1.75} />
              </div>
              <h3 className="text-lg font-medium text-slate-900 tracking-tight">{t("settings.ai_restricted_title")}</h3>
              <p className="text-sm text-slate-500 mt-1.5 mb-5">
                {t("settings.ai_restricted_body")}
              </p>
              <Label className="text-sm font-medium text-slate-700">{t("settings.ai_send_request_to")}</Label>
              <select
                data-testid="ai-instructions-manager-select"
                value={targetManager}
                onChange={(e) => setTargetManager(e.target.value)}
                className="mt-1 h-10 w-full bg-white border border-slate-200 rounded-md px-3 text-sm focus:outline-none focus:border-[var(--bh-accent,#fe7f57)] transition-colors"
              >
                {managers.length === 0 ? (
                  <option value="">{t("settings.ai_no_managers")}</option>
                ) : (
                  managers.map((m) => (
                    <option key={m.user_id} value={m.user_id}>
                      {m.name || m.email} ({m.role})
                    </option>
                  ))
                )}
              </select>
              <Button
                data-testid="ai-instructions-send-request"
                onClick={sendRequest}
                disabled={sending || !targetManager}
                className="w-full mt-5 h-10 rounded-md bg-slate-900 text-white hover:bg-slate-800 text-sm font-medium disabled:opacity-60"
              >
                {sending ? t("settings.ai_sending") : t("settings.ai_send_request")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
